import PageHeader from "@/components/common/PageHeader";
import { normalizeApiBaseUrl } from "@medicorepay/shared/lib/use-version-check";
import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle,
  Globe,
  Monitor,
  RefreshCw,
  Server,
  Shield,
  Smartphone,
  XCircle,
} from "lucide-react";
import { useState } from "react";

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface VersionInfo {
  api: {
    version: string;
    minClientVersion: string;
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function parseSemver(v: string): [number, number, number] {
  const parts = v.replace(/^v/, "").split(".").map(Number);
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0];
}

function compareSemver(a: string, b: string): number {
  const pa = parseSemver(a);
  const pb = parseSemver(b);
  for (let i = 0; i < 3; i++) {
    if (pa[i] > pb[i]) return 1;
    if (pa[i] < pb[i]) return -1;
  }
  return 0;
}

const platformIcons: Record<string, typeof Globe> = {
  web: Globe,
  desktop: Monitor,
  mobile: Smartphone,
  admin: Shield,
};

const platformLabels: Record<string, string> = {
  web: "POS Web",
  desktop: "POS Desktop",
  mobile: "POS Mobile",
  admin: "Admin Portal",
};

function CompatBadge({
  version,
  minVersion,
}: {
  version: string;
  minVersion: string;
}) {
  const isOk = compareSemver(version, minVersion) >= 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isOk ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
      }`}
    >
      {isOk ? <CheckCircle size={12} /> : <XCircle size={12} />}
      {isOk ? "Compatible" : "Outdated"}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// API HELPERS
// ═══════════════════════════════════════════════════════════════════════════

const API_URL = (
  import.meta.env.VITE_ADMIN_API_URL ||
  import.meta.env.VITE_API_URL ||
  "http://localhost:5001"
).replace(/\/+$/, "");

const API_BASE = normalizeApiBaseUrl(API_URL);

async function fetchApiVersion(): Promise<VersionInfo> {
  const resp = await fetch(`${API_BASE}/api/v1/health/version`);
  if (!resp.ok) throw new Error("Failed to fetch API version");
  return resp.json();
}

async function fetchHealthReady(): Promise<any> {
  const resp = await fetch(`${API_BASE}/api/v1/health/ready`);
  if (!resp.ok) throw new Error("Failed to fetch health");
  return resp.json();
}

// ═══════════════════════════════════════════════════════════════════════════
// MAIN PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function VersionDashboardPage() {
  const [refreshKey, setRefreshKey] = useState(0);

  const versionQuery = useQuery({
    queryKey: ["admin", "version-info", refreshKey],
    queryFn: fetchApiVersion,
    staleTime: 60_000,
  });

  const healthQuery = useQuery({
    queryKey: ["admin", "health-ready", refreshKey],
    queryFn: fetchHealthReady,
    staleTime: 60_000,
  });

  const apiVersion = versionQuery.data?.api;
  const health = healthQuery.data;
  const adminVersion = import.meta.env.VITE_APP_VERSION || "1.0.0";

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Version Management"
        subtitle="Track and verify version compatibility across all MediCorePay apps"
        actions={
          <button
            onClick={() => setRefreshKey((k) => k + 1)}
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        }
      />

      {/* API Version Card */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Server size={20} className="text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">API Server</h3>
              <p className="text-sm text-slate-500">Backend</p>
            </div>
          </div>
          {versionQuery.isLoading ? (
            <div className="animate-pulse h-8 bg-slate-100 rounded" />
          ) : versionQuery.error ? (
            <p className="text-sm text-red-500">Failed to fetch</p>
          ) : (
            <div>
              <p className="text-2xl font-bold text-slate-900">
                v{apiVersion?.version}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Min client: v{apiVersion?.minClientVersion}
              </p>
              {health && (
                <p
                  className={`text-xs mt-2 font-medium ${
                    health.status === "healthy"
                      ? "text-emerald-600"
                      : health.status === "degraded"
                        ? "text-amber-600"
                        : "text-red-600"
                  }`}
                >
                  Status: {health.status}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Admin Portal Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-purple-50 rounded-lg">
              <Shield size={20} className="text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Admin Portal</h3>
              <p className="text-sm text-slate-500">This app</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-slate-900">v{adminVersion}</p>
          {apiVersion && (
            <div className="mt-2">
              <CompatBadge
                version={adminVersion}
                minVersion={apiVersion.minClientVersion}
              />
            </div>
          )}
        </div>

        {/* Health Summary Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-emerald-50 rounded-lg">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">System Health</h3>
              <p className="text-sm text-slate-500">Real-time</p>
            </div>
          </div>
          {healthQuery.isLoading ? (
            <div className="animate-pulse h-8 bg-slate-100 rounded" />
          ) : healthQuery.error ? (
            <p className="text-sm text-red-500">Failed to fetch</p>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Database</span>
                <StatusDot status={health?.checks?.database?.status} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Redis</span>
                <StatusDot status={health?.checks?.redis?.status} />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">Memory</span>
                <StatusDot status={health?.checks?.memory?.status} />
              </div>
              {health?.uptime && (
                <p className="text-xs text-slate-500 pt-1 border-t">
                  Uptime: {formatUptime(health.uptime)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Version Compatibility Matrix */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-6 border-b border-slate-100">
          <h2 className="text-lg font-semibold text-slate-900">
            Version Compatibility Matrix
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Overview of all app versions and their API compatibility. Update{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">
              versions.json
            </code>{" "}
            at the repo root and run{" "}
            <code className="bg-slate-100 px-1 rounded text-xs">
              pnpm version:sync
            </code>{" "}
            to propagate.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                <th className="px-6 py-3">Platform</th>
                <th className="px-6 py-3">Version</th>
                <th className="px-6 py-3">Min API Required</th>
                <th className="px-6 py-3">API Compatibility</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              <PlatformRow
                platform="api"
                version={apiVersion?.version || "—"}
                minApi="—"
                apiVersion={apiVersion?.version}
                isApi
              />
              <PlatformRow
                platform="web"
                version="—"
                minApi="1.0.0"
                apiVersion={apiVersion?.version}
                note="Set via VITE_APP_VERSION env var"
              />
              <PlatformRow
                platform="desktop"
                version="—"
                minApi="1.0.0"
                apiVersion={apiVersion?.version}
                note="Set via VITE_APP_VERSION env var"
              />
              <PlatformRow
                platform="mobile"
                version="—"
                minApi="1.0.0"
                apiVersion={apiVersion?.version}
                note="Set in pubspec.yaml"
              />
              <PlatformRow
                platform="admin"
                version={adminVersion}
                minApi="1.0.0"
                apiVersion={apiVersion?.version}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* How To Update */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-6">
        <h3 className="font-semibold text-slate-900 mb-3">
          How to manage versions
        </h3>
        <div className="space-y-3 text-sm text-slate-700">
          <div className="flex gap-3">
            <span className="font-mono bg-white px-2 py-0.5 rounded border text-xs shrink-0">
              1
            </span>
            <span>
              Edit{" "}
              <code className="bg-white px-1 rounded text-xs border">
                versions.json
              </code>{" "}
              at the monorepo root — this is the single source of truth. Or use:{" "}
              <code className="bg-white px-1 rounded text-xs border">
                pnpm version:bump &lt;app&gt; &lt;major|minor|patch&gt;
              </code>
            </span>
          </div>
          <div className="flex gap-3">
            <span className="font-mono bg-white px-2 py-0.5 rounded border text-xs shrink-0">
              2
            </span>
            <span>
              Run{" "}
              <code className="bg-white px-1 rounded text-xs border">
                pnpm version:sync
              </code>{" "}
              to propagate versions to all package.json, pubspec.yaml, and
              tauri.conf.json files.
            </span>
          </div>
          <div className="flex gap-3">
            <span className="font-mono bg-white px-2 py-0.5 rounded border text-xs shrink-0">
              3
            </span>
            <span>
              Set the{" "}
              <code className="bg-white px-1 rounded text-xs border">
                APP_VERSION
              </code>{" "}
              (API) and{" "}
              <code className="bg-white px-1 rounded text-xs border">
                VITE_APP_VERSION
              </code>{" "}
              (clients) env vars in your deployment pipeline. Each client app
              checks compatibility with the API on startup.
            </span>
          </div>
          <div className="flex gap-3">
            <span className="font-mono bg-white px-2 py-0.5 rounded border text-xs shrink-0">
              4
            </span>
            <span>
              When you need to force clients to update, bump{" "}
              <code className="bg-white px-1 rounded text-xs border">
                api.minClientVersion
              </code>{" "}
              in versions.json. Old clients will see a "please update" banner.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function PlatformRow({
  platform,
  version,
  minApi,
  apiVersion,
  note,
  isApi,
}: {
  platform: string;
  version: string;
  minApi: string;
  apiVersion?: string;
  note?: string;
  isApi?: boolean;
}) {
  const Icon = platformIcons[platform] || Globe;
  const label = platformLabels[platform] || platform;

  return (
    <tr className="hover:bg-slate-50 transition-colors">
      <td className="px-6 py-4">
        <div className="flex items-center gap-2">
          <Icon size={16} className="text-slate-400" />
          <span className="font-medium text-slate-900">{label}</span>
        </div>
      </td>
      <td className="px-6 py-4">
        <span className="font-mono text-sm">
          {version !== "—" ? `v${version}` : "—"}
        </span>
        {note && <p className="text-xs text-slate-400 mt-0.5">{note}</p>}
      </td>
      <td className="px-6 py-4">
        <span className="font-mono text-sm text-slate-600">
          {minApi !== "—" ? `>= v${minApi}` : "—"}
        </span>
      </td>
      <td className="px-6 py-4">
        {isApi ? (
          <span className="text-xs text-slate-400">N/A (this is the API)</span>
        ) : apiVersion && version !== "—" ? (
          <CompatBadge version={apiVersion} minVersion={minApi} />
        ) : apiVersion ? (
          <span className="text-xs text-slate-400">
            Checked at client startup
          </span>
        ) : (
          <span className="text-xs text-slate-400">Loading...</span>
        )}
      </td>
    </tr>
  );
}

function StatusDot({ status }: { status?: "up" | "down" }) {
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium ${
        status === "up"
          ? "text-emerald-600"
          : status === "down"
            ? "text-red-600"
            : "text-slate-400"
      }`}
    >
      <span
        className={`w-2 h-2 rounded-full ${
          status === "up"
            ? "bg-emerald-500"
            : status === "down"
              ? "bg-red-500"
              : "bg-slate-300"
        }`}
      />
      {status || "—"}
    </span>
  );
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}
